import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { freezeObject, objectHasDifference } from '../helper/functions';
import { FormModel, QuestionForm } from '../models/questionModel';

export interface Form {
  liquidation: FormModel;
  funding: FormModel;
  credit: FormModel;
  commodity: FormModel;
  currency: FormModel;
  interest: FormModel;
  operational: FormModel;
  disaster: FormModel;
  compliance: FormModel;
  legal: FormModel;
}

export interface QuestionStore {
  data: Form;
  setQuestion: (_data: Partial<Form>) => void;
  resetQuestion: () => void;
  formDirty: (_data: Partial<Form>) => boolean;
  getQuestionByForm: (_: FormType) => FormModel;
}

export type FormType =
  | 'liquidation'
  | 'funding'
  | 'credit'
  | 'commodity'
  | 'currency'
  | 'interest'
  | 'operational'
  | 'disaster'
  | 'compliance'
  | 'legal';

const defaultQuestion: Form = {
  liquidation: new QuestionForm(),
  funding: new QuestionForm(),
  credit: new QuestionForm(),
  commodity: new QuestionForm(),
  currency: new QuestionForm(),
  interest: new QuestionForm(),
  operational: new QuestionForm(),
  disaster: new QuestionForm(),
  compliance: new QuestionForm(),
  legal: new QuestionForm(),
};

const useQuestionStore = create<QuestionStore>()(
  persist(
    (set, get) => ({
      data: freezeObject(defaultQuestion),
      setQuestion: (value: Partial<Form>) => {
        set({ data: { ...get().data, ...value } });
      },
      resetQuestion: () => set(freezeObject(defaultQuestion)),
      formDirty: (value: Partial<Form>) => {
        return objectHasDifference(defaultQuestion, value);
      },
      getQuestionByForm: (type: FormType): FormModel => {
        const question = get().data[type];
        const questionForm = new QuestionForm(
          question.title,
          question.description,
          question.compulsoryQuestions,
          question.impactQuestions,
          question.probabilityQuestions
        );
        questionForm.marksNotes = question.marksNotes || [];

        return questionForm;
      },
    }),
    {
      name: 'question-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useQuestionStore;
